import {
  Button,
  type CreateToastFnReturn,
  Divider,
  Flex,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FaGoogle } from 'react-icons/fa';
import {
  type Auth,
  type AuthProvider,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth';
import { type NavigateFunction, useNavigate } from 'react-router';
import { errorToastOptions, getAuthErrorMessage } from '@/helpers';
import type { FirebaseError } from 'firebase/app';
import { useSearchParams } from 'react-router-dom';
import { useAuthTheme } from './auth-ui';

export { EmailDeliveryHint } from './auth-ui';

const GAUTH_PROVIDER: AuthProvider = new GoogleAuthProvider().setCustomParameters({ prompt: 'select_account' });

export const handleGoogle = (
  auth: Auth,
  navigate: NavigateFunction,
  toast: CreateToastFnReturn,
  redirectTo: string | null,
) => {
  signInWithPopup(auth, GAUTH_PROVIDER)
    .then(() => {
      if (redirectTo !== null) {
        navigate(redirectTo);
      } else {
        navigate('/profile');
      }
    })
    .catch((error: FirebaseError) => {
      if (error.code === 'auth/popup-closed-by-user') {
        return;
      }
      toast(errorToastOptions('Sign in failed', getAuthErrorMessage(error)));
    });
};

export const _handleEmailLogin = async (auth: Auth, toast: CreateToastFnReturn, email: string, password: string) => {
  return await signInWithEmailAndPassword(auth, email, password)
    .then(() => true)
    .catch((error: FirebaseError) => {
      toast(errorToastOptions('Sign in failed', getAuthErrorMessage(error)));
      return false;
    });
};

export function AuthDivider() {
  const { dividerColor, mutedText } = useAuthTheme();

  return (
    <Flex align='center' py={5}>
      <Divider flex={1} borderColor={dividerColor} />
      <Text
        fontSize='xs'
        fontWeight='semibold'
        letterSpacing='wider'
        px={3}
        color={mutedText}
        whiteSpace='nowrap'
      >
        OR
      </Text>
      <Divider flex={1} borderColor={dividerColor} />
    </Flex>
  );
}

export function GoogleButton({ auth, children }: { auth: Auth; children: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();

  return (
    <Button
      onClick={() => handleGoogle(auth, navigate, toast, searchParams.get('redirect_to'))}
      width='full'
      size='md'
      leftIcon={<FaGoogle />}
      bg='red.500'
      color='white'
      fontWeight='semibold'
      borderRadius='md'
      _hover={{ bg: 'red.600' }}
      _active={{ bg: 'red.700' }}
    >
      {children}
    </Button>
  );
}
